import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Clock, X, Headphones } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getEmbedUrl(url) {
  if (!url) return null;
  if (url.includes('youtube.com/watch?v=')) {
    try { const videoId = new URL(url).searchParams.get('v'); return `https://www.youtube.com/embed/${videoId}?autoplay=1`; } catch { return null; }
  }
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
  }
  if (url.includes('spotify.com/episode/')) {
    const epId = url.split('/episode/')[1]?.split('?')[0];
    return epId ? `https://open.spotify.com/embed/episode/${epId}` : null;
  }
  return url;
}

function getYTThumbnail(url) {
  if (url?.includes('youtube.com/watch?v=')) {
    try { const v = new URL(url).searchParams.get('v'); return `https://img.youtube.com/vi/${v}/mqdefault.jpg`; } catch { return null; }
  }
  if (url?.includes('youtu.be/')) {
    const v = url.split('youtu.be/')[1]?.split('?')[0];
    return v ? `https://img.youtube.com/vi/${v}/mqdefault.jpg` : null;
  }
  return null;
}

export default function PodcastSection() {
  const [podcasts, setPodcasts] = useState([]);
  const [playing, setPlaying] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/podcasts`).then(r => setPodcasts(r.data)).catch(() => {});
  }, []);

  if (podcasts.length === 0) return null;

  return (
    <section id="podcasts" className="py-14 sm:py-20 px-4 sm:px-6 border-t" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Headphones className="w-4 h-4" style={{ color: '#D97A46' }} />
            <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#D97A46' }}>{t('podcast.label')}</p>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl font-bold" style={{ color: 'var(--c-text)' }}>{t('podcast.title')}</h2>
          <p className="text-base mt-2 max-w-xl mx-auto" style={{ color: 'var(--c-muted)' }}>{t('podcast.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {podcasts.map((pod, i) => {
            const thumb = pod.thumbnail || getYTThumbnail(pod.embedUrl);
            return (
              <motion.div key={pod.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-2xl overflow-hidden group cursor-pointer hover:-translate-y-1 transition-transform duration-300 border"
                style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: 'var(--c-shadow-md)' }}
                data-testid={`podcast-card-${pod.id}`}
              >
                <div className="relative h-44 overflow-hidden" style={{ backgroundColor: 'var(--c-surface-alt)' }} onClick={() => setPlaying(pod)}>
                  {thumb
                    ? <img src={thumb} alt={pod.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--c-surface-alt)' }}><Headphones className="w-12 h-12" style={{ color: 'var(--c-border)' }} /></div>
                  }
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: '#D97A46' }}>
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-base font-semibold leading-tight mb-1.5 line-clamp-2" style={{ color: 'var(--c-text)' }}>{pod.title}</h3>
                  <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'var(--c-muted)' }}>{pod.description}</p>
                  {pod.duration && (
                    <div className="flex items-center gap-1.5 mt-3 text-xs" style={{ color: '#D97A46' }}>
                      <Clock className="w-3.5 h-3.5" /> {pod.duration}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Player Modal */}
      <AnimatePresence>
        {playing && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80]" onClick={() => setPlaying(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[90] w-full max-w-2xl px-4"
              data-testid="podcast-player-modal"
            >
              <div className="rounded-2xl overflow-hidden border shadow-2xl" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}>
                <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--c-border)' }}>
                  <h3 className="font-serif font-semibold text-sm line-clamp-1" style={{ color: 'var(--c-text)' }}>{playing.title}</h3>
                  <button onClick={() => setPlaying(null)} data-testid="close-podcast-player"
                    className="p-1 rounded-full transition-colors hover:text-[#D97A46]" style={{ color: 'var(--c-muted)' }}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {getEmbedUrl(playing.embedUrl) ? (
                  <iframe src={getEmbedUrl(playing.embedUrl)} className="w-full aspect-video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen title={playing.title} />
                ) : (
                  <div className="p-6 text-center" style={{ color: 'var(--c-muted)' }}>
                    <a href={playing.embedUrl} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: '#D97A46' }}>{t('podcast.openInTab')}</a>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
